import { Box, LoadingOverlay, Stepper } from '@mantine/core';
import { IconArrowBearRight2, IconAsset, IconForms, IconSchema } from '@tabler/icons-react';
import { CancelButton } from 'components/CancelButton';
import { PageTitle } from 'components/PageTitle';
import { SaveButton } from 'components/SaveButton';
import { i18nKeys, useTranslation } from 'locales';
import { Step1, Step2, Step3, Step4 } from './components';
import { getAutomationFormInitialValues, useAutomationWizardController } from './controller';
import {
  AutomationWizardFormProvider,
  useAutomationWizardForm,
} from './controller/automation-wizard-form';
import type { AutomationWizardStep } from './controller/types';

const AutomationWizardInner = () => {
  const { t } = useTranslation();
  const {
    form,
    navigation: {
      handleSave,
      cancelWizard,
      isStepAccessible,
      isEditMode,
      step,
      goToStep,
      isAutomationValid,
      isWizardLoading,
    },
  } = useAutomationWizardController();

  return (
    <>
      <PageTitle>
        {isEditMode
          ? t(i18nKeys.AUTOMATION.WIZARD.TITLE.EDIT)
          : t(i18nKeys.AUTOMATION.WIZARD.TITLE.CREATE)}
        <PageTitle.Actions>
          {isEditMode && (
            <SaveButton
              disabled={!form.isDirty() || !isAutomationValid()}
              variant="light"
              onClick={handleSave}
            />
          )}
          <CancelButton onClick={cancelWizard} />
        </PageTitle.Actions>
      </PageTitle>
      <Stepper
        active={step.current}
        onStepClick={newStep => goToStep(newStep as AutomationWizardStep)}
      >
        <Stepper.Step
          allowStepSelect
          label={t(i18nKeys.AUTOMATION.WIZARD.STEPS.NAME)}
          icon={<IconForms size={20} stroke={1.5} />}
          completedIcon={<IconForms size={20} stroke={1.5} />}
        >
          <Box pos="relative">
            <LoadingOverlay visible={isWizardLoading} />
            <Step1 />
          </Box>
        </Stepper.Step>
        <Stepper.Step
          allowStepSelect={isStepAccessible(1)}
          label={t(i18nKeys.AUTOMATION.WIZARD.STEPS.MODEL)}
          icon={<IconSchema size={20} stroke={1.5} />}
          completedIcon={<IconSchema size={20} stroke={1.5} />}
        >
          <Box pos="relative">
            <LoadingOverlay visible={isWizardLoading} />
            <Step2 />
          </Box>
        </Stepper.Step>
        <Stepper.Step
          allowStepSelect={isStepAccessible(2)}
          label={t(i18nKeys.AUTOMATION.WIZARD.STEPS.CONDITIONS)}
          icon={<IconArrowBearRight2 size={20} stroke={1.5} />}
          completedIcon={<IconArrowBearRight2 size={20} stroke={1.5} />}
        >
          <Box pos="relative">
            <LoadingOverlay visible={isWizardLoading} />
            <Step3 />
          </Box>
        </Stepper.Step>
        <Stepper.Step
          allowStepSelect={isStepAccessible(3)}
          label={t(i18nKeys.AUTOMATION.WIZARD.STEPS.ACTIONS)}
          icon={<IconAsset size={20} stroke={1.5} />}
          completedIcon={<IconAsset size={20} stroke={1.5} />}
        >
          <Box pos="relative">
            <LoadingOverlay visible={isWizardLoading} />
            <Step4 />
          </Box>
        </Stepper.Step>
      </Stepper>
    </>
  );
};

export const AutomationWizard = () => {
  const automationWizardForm = useAutomationWizardForm({
    cascadeUpdates: true,
    initialValues: getAutomationFormInitialValues(),
  });

  return (
    <AutomationWizardFormProvider form={automationWizardForm}>
      <AutomationWizardInner />
    </AutomationWizardFormProvider>
  );
};
